// src/sim/bot/dummy.ts — Eğitim kuklası (docs/TUTORIAL.md §2 "Kukla davranış modları"). `Bot` arayüzünü (core/types.ts) birebir
// uygular; oyun döngüsü kuklayı gerçek bottan ayırt etmez (`nextThinkTick` → `think` → `frame`, aynı InputFrame yolu, hile yok).
//
// Modlar (TUTORIAL §2 tablosu):
//   'idle'    ("bekle")    → hiç girdi üretmez: boş InputFrame, çubuk 0, tuş yok. Yerinden kıpırdamaz. Bakışı `faceDummy` çevirir.
//   'attacks' ("saldırır") → 150 t'de bir TEK hamle, sırayla L1_jab (X) → H1_kick (Y) → L1_jab… Aralarda 1.2–1.6 u mesafeyi
//                            korur (yalnız fazla yaklaşılınca geri çekilir), KOVALAMAZ. Ritim tamamen deterministik: zar yok.
//   'fights'  ("dövüşür")  → gerçek kolay bot (`createBot(id, 'easy', seed)`) sarmalanır; tüm alanlar ona devredilir.
//
// Kurallar: rastgelelik yalnız `hashNoise` üzerinden olabilir — bu dosyada hiç zar yok (ritim deterministik, TUTORIAL §2),
// Math.random / Date.now yok, three.js / DOM import edilmez, `think`/`frame` sıcak yolda tahsis yapmaz (scratch alanlar sınıfta
// ve modül düzeyinde). Sim dosyaları DEĞİŞTİRİLMEZ: kuklanın düşmemesi/ölmemesi oyun katmanının her tick çağırdığı saf
// yardımcılarla (`keepDummyAlive`, `faceDummy`, `resetDummy`) sağlanır.
import { Btn, FState } from '../../core/types.ts';
import type { Bot, BotParams, Difficulty, InputFrame, SimWorld, Vec3 } from '../../core/types.ts';
import { inputFrame } from '../../core/types.ts';
import { v3, yawOf } from '../../core/vec3.ts';
import { CAP_R, NO_ID, NO_TICK, TICK_DT } from '../../config/constants.ts';
import { T } from '../../config/tuning.ts';
import { createBot, botParamsOf } from './brain.ts';
import type { BotBrain } from './brain.ts';
import { createPerception, perceive } from './perception.ts';
import type { BotPerception } from './perception.ts';

/** Kukla davranış modu (TUTORIAL §2: bekle / saldırır / dövüşür). */
export type DummyMode = 'idle' | 'attacks' | 'fights';

// ── Eğitim sabitleri (yalnız eğitim modunda geçerli; T.* karşılığı yok → yerel, brain.ts'teki "needsWP0" deseniyle aynı) ──
/** TUTORIAL §2: "150 t'de (2.5 s) bir tek hamle". İki vuruş arasındaki tam süre. */
export const DUMMY_ATTACK_PERIOD = 150;
/** Mod 'attacks'e geçtikten sonraki ilk vuruşa kadar bekleme (1.5 s): oyuncu paneli okusun, ders vuruşla başlamasın. */
export const DUMMY_FIRST_DELAY = 90;
/** TUTORIAL §2: "Aralarda 1.2–1.6 u mesafeyi korur, kovalamaz." Bu bandın ALTINDA geri çekilir, ÜSTÜNDE yaklaşmaz. */
export const DUMMY_KEEP_MIN = 1.2;
export const DUMMY_KEEP_MAX = 1.6;
/** Vuruş penceresi (u): oyuncu bundan uzaktaysa vuruş atılmaz — havaya sallamaz, ritim gridi bozulmaz (vuruş ertelenir).
 *  L1_jab erişimi 0.98 + hurtbox 0.45 ≈ 1.43; pay bırakılır ki oyuncu bir adım geriye kaçtığında ders durmasın. */
export const DUMMY_ATTACK_RANGE = 2.0;
/** Geri çekilme çubuğu: walkDead üstü (yürür) ama runStickMin 0.85 altı (koşma bağlamı açılmaz → Dash/koşu hamlesi yok). */
const BACK_STICK = 0.5;
/** Bir dokunuşun basılı kalma süresi (t): sim `pressed` kenarını okur. Y için de 2 t — heavyHoldTicks (15 t) altında kalır,
 *  yani ıska takibi (H1 → H2 Sweep) TETİKLENMEZ: kukla hep tek ve aynı tekmeyi atar (öğretilebilir tell). */
const TAP_HOLD = 2;
/** Geri çekilirken kenar kontrolü öngörüsü (t): bu kadar tick sonra platform dışına çıkacaksa geri çekilme iptal (kukla düşmez). */
const BACK_LOOKAHEAD = 12;
/** Kenar freni payı (u): kuklanın merkezi kenara bundan yakın olamaz (kapsül yarıçapı + pay). */
const EDGE_MARGIN = CAP_R + 0.1;
/** Bu yüksekliğin altına düşen kukla eve ışınlanır (KILL_Y = −8'e hiç ulaşmaz → Ev.Fell / eleme yok). */
const CATCH_Y = -1;
/** `groundY` sorgusunun başlangıç yüksekliği (eve ışınlarken zemini bulmak için). */
const HOME_PROBE_Y = 5;
/** Bakışın çevrilebildiği durumlar: hamlenin/ sersemlemenin içinde yaw'a dokunulmaz (sim'in nişanı bozulmasın). */
const CAN_FACE = (s: FState): boolean => s === FState.Idle || s === FState.Move || s === FState.Block;
/** Vuruş atılabilen durumlar: ayakta ve serbest. Aksi hâlde ritim ertelenir (vuruş kaybolmaz). */
const CAN_ACT = (s: FState): boolean => s === FState.Idle || s === FState.Move;

/** Modül scratch'leri (tahsissiz): kenar normali ve ışınlama. */
const tmpN: Vec3 = v3();

export class DummyBot implements Bot {
  readonly fighterId: number;
  seed: number;
  /** Aktif mod; ders değişince `setMode` ile değiştirilir. */
  private modeVal: DummyMode;
  /** 'fights' modunun gerçek beyni. Her zaman kurulur (mod anında değişebilsin), yalnız o modda çalıştırılır. */
  private readonly brain: BotBrain;

  // ── 'idle' / 'attacks' durumu ──
  private readonly ownIntent: InputFrame = inputFrame();
  private readonly ownPerception: BotPerception = createPerception();
  private ownNextThink = 0;
  /** Oyuncunun (hedefin) id'si; NO_ID → algı en yakın düşmana düşer. `setTarget` ile verilir. */
  targetId = NO_ID;
  targetSince = NO_TICK;
  whiffWaitUntil = NO_TICK;
  reactAt = NO_TICK;
  parryTapAt = NO_TICK;

  /** Sıradaki vuruşun tick'i (NO_TICK = ritim henüz kurulmadı: ilk think kurar). */
  private nextAttackAt = NO_TICK;
  /** Vuruş sayacı: çift → X (L1_jab), tek → Y (H1_kick). Sıra deterministik, zar yok. */
  private beat = 0;
  /** Son think'in kararı: bu tick vuruş atılabilir mi (hedef menzilde ∧ kukla serbest). */
  private armed = false;

  // ── dokunuş makinesi (tek tuş yeter: iki vuruş arası 150 t) ──
  private holdBtn = 0;
  private holdLeft = 0;
  private prevButtons = 0;

  constructor(fighterId: number, mode: DummyMode, seed: number) {
    this.fighterId = fighterId;
    this.modeVal = mode;
    this.seed = seed;
    this.brain = createBot(fighterId, 'easy', seed);
  }

  // ───────────────────────── mod / ders denetimi ─────────────────────────
  get mode(): DummyMode { return this.modeVal; }
  /** Ders değişiminde çağrılır: modu değiştirir ve ritmi sıfırlar (ilk vuruş yeniden DUMMY_FIRST_DELAY sonra). */
  setMode(mode: DummyMode, tick: number): void {
    if (mode !== this.modeVal) { this.releaseAll(); this.modeVal = mode; }
    this.resetRhythm(tick);
  }
  /** Hedefi (oyuncuyu) verir; NO_ID → en yakın düşman. */
  setTarget(id: number): void { this.targetId = id; }
  /** Ritmi baştan kurar (ders başı, `R` ile yeniden başlatma, oyuncu düşüp geri geldiğinde). */
  resetRhythm(tick: number): void {
    this.nextAttackAt = tick + DUMMY_FIRST_DELAY;
    this.beat = 0;
    this.armed = false;
  }
  /** Basılı kalan tuşu bırakır (mod değişiminde: yarım kalan dokunuş bir sonraki moda taşınmasın). */
  private releaseAll(): void { this.holdBtn = 0; this.holdLeft = 0; }

  // ───────────────────────── Bot arayüzü: devredilen alanlar ─────────────────────────
  get difficulty(): Difficulty { return 'easy'; }
  get params(): BotParams { return this.modeVal === 'fights' ? this.brain.params : botParamsOf('easy'); }
  set params(p: BotParams) { this.brain.params = p; }
  get intent(): InputFrame { return this.modeVal === 'fights' ? this.brain.intent : this.ownIntent; }
  get perception(): BotPerception { return this.modeVal === 'fights' ? this.brain.perception : this.ownPerception; }
  get nextThinkTick(): number { return this.modeVal === 'fights' ? this.brain.nextThinkTick : this.ownNextThink; }
  set nextThinkTick(t: number) { if (this.modeVal === 'fights') this.brain.nextThinkTick = t; else this.ownNextThink = t; }

  // ───────────────────────── think ─────────────────────────
  /** Her T.BOT.thinkTicks tick'te (oyun döngüsü `nextThinkTick` ile çağırır). Tahsissiz. */
  think(world: SimWorld): void {
    if (this.modeVal === 'fights') { this.brain.think(world); return; }
    const tick = world.tick;
    this.ownNextThink = tick + T.BOT.thinkTicks;
    const f = world.fighters[this.fighterId];
    if (!f) return;

    const p = this.ownPerception;
    perceive(world, f, this.targetId, p);

    // niyet: her think'te sıfırdan (dokunuş kuyruğu frame()'de, burada değil)
    this.ownIntent.moveX = 0; this.ownIntent.moveZ = 0; this.ownIntent.buttons = 0;
    this.armed = false;
    if (this.modeVal === 'idle') return;                       // "bekle": hiç girdi yok

    // ── "saldırır" ──
    if (p.targetId === NO_ID) return;                          // hedef yok: dur, bekle
    // ritim kurulmadıysa (setMode çağrılmadan kullanıldıysa) burada kurulur — deterministik
    if (this.nextAttackAt === NO_TICK) this.nextAttackAt = tick + DUMMY_FIRST_DELAY;
    this.armed = CAN_ACT(f.state) && f.grounded && p.targetDist <= DUMMY_ATTACK_RANGE;

    // mesafe koruma: yalnız fazla yaklaşıldıysa geri çekil (kovalama YOK — uzaktaki oyuncunun peşine düşmez).
    // Bakış `faceDummy` ile (adım sonrası) oyuncuya döndürüldüğünden geri çekilirken de yüzü oyuncuya dönüktür.
    if (p.targetDist < DUMMY_KEEP_MIN && CAN_ACT(f.state) && f.grounded) {
      const bx = -p.dirX, bz = -p.dirZ;
      // kenar kontrolü: geri çekilme platform dışına götürüyorsa yerinde kal (TUTORIAL §1 "Kukla düşmez")
      const sec = BACK_LOOKAHEAD * TICK_DT;
      const px = f.pos.x + bx * T.MOVE.runSpeed * BACK_STICK * sec;
      const pz = f.pos.z + bz * T.MOVE.runSpeed * BACK_STICK * sec;
      if (world.query.isInside(px, pz) && world.query.edgeDist(px, pz, tmpN) > EDGE_MARGIN) {
        this.ownIntent.moveX = bx * BACK_STICK; this.ownIntent.moveZ = bz * BACK_STICK;
      }
    }
  }

  // ───────────────────────── frame ─────────────────────────
  /** Her tick: niyet + ritim → InputFrame. pressed/released yalnız değişim tick'inde. Tahsissiz. */
  frame(out: InputFrame, tick: number): void {
    if (this.modeVal === 'fights') { this.brain.frame(out, tick); return; }

    // vuruş ritmi: gridin üstünde ve hazırsa bas; hazır değilse ERTELE (vuruş kaybolmaz, sıra bozulmaz)
    if (this.modeVal === 'attacks' && this.armed && this.nextAttackAt !== NO_TICK && tick >= this.nextAttackAt) {
      this.holdBtn = (this.beat & 1) === 0 ? Btn.Light : Btn.Heavy;   // sırayla L1_jab → H1_kick → L1_jab…
      this.holdLeft = TAP_HOLD;
      this.beat++;
      this.nextAttackAt = tick + DUMMY_ATTACK_PERIOD;
      this.armed = false;                                            // aynı think penceresinde ikinci vuruş yok
    }

    let buttons = 0;
    if (this.holdLeft > 0) { buttons |= this.holdBtn; this.holdLeft--; }

    out.buttons = buttons;
    out.pressed = buttons & ~this.prevButtons;
    out.released = this.prevButtons & ~buttons;
    this.prevButtons = buttons;
    out.moveX = this.ownIntent.moveX; out.moveZ = this.ownIntent.moveZ;
    out.aimYaw = NaN; out.fromMouse = false;
  }
}

/** Kukla yaratır (TUTORIAL §2). `seed` yalnız 'fights' modunda (sarmalanan kolay bot) anlamlıdır; 'idle'/'attacks' deterministiktir. */
export function createDummy(fighterId: number, mode: DummyMode, seed: number = fighterId + 1): DummyBot {
  return new DummyBot(fighterId, mode, seed);
}

// ───────────────────────────── oyun katmanı yardımcıları (sim DEĞİŞMEZ) ─────────────────────────────

/**
 * TUTORIAL §2: kukla "sadece durur ve oyuncuya döner". Bakışı `turnRate` hızıyla hedefe çevirir — yalnız serbest durumlarda
 * (Idle / Move / Block); hamlede, sersemlemede, yerdeyken yaw'a DOKUNULMAZ (sim'in nişanı ve klipleri bozulmasın).
 * Her tick, `world.step()` SONRASI çağrılır (adım 6 çubuk yönüne dönerken bunu ezer; çubuk 0 iken sim yaw'a dokunmaz).
 * Dönüş: yaw değiştirildi mi.
 */
export function faceDummy(world: SimWorld, dummyId: number, targetId: number): boolean {
  const f = world.fighters[dummyId];
  const t = targetId !== NO_ID ? world.fighters[targetId] : undefined;
  if (!f || !t || t.id === f.id) return false;
  if (!CAN_FACE(f.state)) return false;
  const dx = t.pos.x - f.pos.x, dz = t.pos.z - f.pos.z;
  if (dx === 0 && dz === 0) return false;
  const target = yawOf(dx, dz);
  const step = T.MOVE.turnRate * TICK_DT;
  const TAU = Math.PI * 2;
  let d = (target - f.yaw) % TAU;
  if (d > Math.PI) d -= TAU; else if (d <= -Math.PI) d += TAU;
  const ad = d < 0 ? -d : d;
  if (ad <= step) { f.yaw = target; return ad > 0; }
  let y = (f.yaw + (d > 0 ? step : -step)) % TAU;
  if (y > Math.PI) y -= TAU; else if (y <= -Math.PI) y += TAU;
  f.yaw = y;
  return true;
}

/**
 * TUTORIAL §1: "Kukla düşmez." Kenar freni kukla için KOŞULSUZ uygulanır (dış bileşen eşiği yok: kenarda hep durur) ve
 * savrulma onu platform dışına taşımaz. Her tick, `world.step()` SONRASI çağrılır.
 * `kenara-at` dersinde (koruma kapalı) ÇAĞRILMAZ — düşme koruması yalnız bu çağrıdan gelir.
 * `homeX/homeZ` = kuklanın ders başındaki yeri; bir şekilde düşerse (ör. koruma açılmadan önce) oraya ışınlanır.
 * Dönüş: bu tick müdahale edildi mi (kenarda tutuldu ya da eve alındı).
 */
export function keepDummyAlive(world: SimWorld, dummyId: number, homeX: number, homeZ: number): boolean {
  const f = world.fighters[dummyId];
  if (!f) return false;

  // (1) zeminin altına düştüyse eve al: KILL_Y'ye (−8) hiç ulaşmaz → Ev.Fell / can eksilmesi / eleme olmaz
  if (f.pos.y < CATCH_Y) {
    const gy = world.query.groundY(homeX, homeZ, HOME_PROBE_Y);
    f.pos.x = homeX; f.pos.z = homeZ; f.pos.y = Number.isNaN(gy) ? 0 : gy;
    // view prev/cur interpolasyonu ışınlanmayı uzun bir kayma olarak çizmesin
    f.prevPos.x = f.pos.x; f.prevPos.y = f.pos.y; f.prevPos.z = f.pos.z;
    f.vel.x = 0; f.vel.y = 0; f.vel.z = 0;
    f.grounded = true; f.lastGroundedTick = world.tick;
    f.fastFall = false; f.fastFallTick = NO_TICK;
    f.tumbleControl = false; f.fellAt = NO_TICK;
    return true;
  }

  // (2) koşulsuz kenar freni: merkez kenara EDGE_MARGIN'dan yakın olamaz, dışa hız sıfırlanır
  const d = world.query.edgeDist(f.pos.x, f.pos.z, tmpN);
  if (d > EDGE_MARGIN) return false;
  const nx = tmpN.x, nz = tmpN.z;
  let touched = false;
  const outV = f.vel.x * nx + f.vel.z * nz;
  if (outV > 0) { f.vel.x -= nx * outV; f.vel.z -= nz * outV; touched = true; }
  const push = EDGE_MARGIN - d;                      // d < 0 ise (dışarıda) içeri çeker
  if (push > 0) { f.pos.x -= nx * push; f.pos.z -= nz * push; touched = true; }
  return touched;
}

/**
 * TUTORIAL §1: "Kukla toparlanır." Hasar yüzdesi, sersemliği ve guard barı tazelenir — ders başında ve ders içinde
 * 360 t (6 s) vuruşsuzlukta çağrılır. Konuma ve duruma DOKUNMAZ (yatıyorsa normal kalkar).
 */
export function resetDummy(world: SimWorld, dummyId: number): void {
  const f = world.fighters[dummyId];
  if (!f) return;
  f.damage = 0;
  f.stun = 0; f.stunHoldUntil = NO_TICK;
  f.guard = f.hasShield ? T.BLOCK.shieldGuardMax : T.BLOCK.guardMax;
  f.guardLastHitTick = NO_TICK;
  f.comboOnMe = 0; f.hitstunTicks = 0;
  f.lastHitBy = NO_ID; f.lastHitTick = NO_TICK;
  for (let i = 0; i < f.recentHitTicks.length; i++) f.recentHitTicks[i] = NO_TICK;
  f.recentHead = 0;
}

/** Kukla son `ticks` tick içinde hiç vurulmadı mı (TUTORIAL §1: 360 t vuruşsuzlukta `resetDummy`). Saf sorgu. */
export function dummyUntouchedFor(world: SimWorld, dummyId: number, ticks: number): boolean {
  const f = world.fighters[dummyId];
  if (!f) return false;
  return f.lastHitTick === NO_TICK || world.tick - f.lastHitTick >= ticks;
}
